import { router } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { audienceMissionPath } from "@/routes"
import type { MissionAudience } from "@/types/missions"

const HELPER_ID = "mission-audience-toggle-helper"

interface AudienceToggleProps {
  missionId: number
  audience: MissionAudience
}

/**
 * OKS-485 — Explicit "Assign to all crew" audience toggle on the brand
 * `Brand/Missions/Show` header. Decoupled from the OKS-30 Assign flow:
 * PATCHes `mission.audience` directly via `audienceMissionPath` so a brand
 * can mark a mission "All crew" with an EMPTY crew (the headline fix —
 * the OKS-467 implicit flip rode the Assign confirm, unreachable when the
 * crew is empty).
 *
 * Behaviour (UX Spec §2–§4):
 *   - `specific → all_crew`: additive, non-destructive → fires immediately,
 *     no confirm.
 *   - `all_crew → specific`: un-mark stops FUTURE auto-assign only; members
 *     already assigned stay (no retroactive unassign). A light AlertDialog
 *     sets that expectation before the PATCH.
 *   - Optimistic: the switch flips instantly and is disabled in-flight to
 *     block double-fire; on error it reverts to the prior server value and
 *     toasts. Server truth is re-read on the redirect that follows success.
 *
 * Rendered only when the viewer may write audience (`can_set_audience` on
 * the Show props). The read-only "All crew" badge stays beside it.
 *
 * NOT a shadcn `Form` / react-hook-form / zod surface — a single-field
 * toggle drives a programmatic `router.patch` (Crew form rules).
 */
export function AudienceToggle({ missionId, audience }: AudienceToggleProps) {
  const { t } = useTranslation()
  // Optimistic view of the switch; reconciled to the `audience` prop on the
  // post-PATCH redirect. Initialised from the server value.
  const [checked, setChecked] = useState(audience === "all_crew")
  const [submitting, setSubmitting] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  function patchAudience(next: MissionAudience) {
    setSubmitting(true)
    setChecked(next === "all_crew")

    router.patch(
      audienceMissionPath(missionId),
      { audience: next },
      {
        preserveScroll: true,
        preserveState: true,
        // Success toast is owned by the server `flash[:success_key]` →
        // `useFlash` (locale-correct, fires on the redirect target), so we
        // don't double-toast here.
        onError: () => {
          // Revert the optimistic flip to the last known server value +
          // surface the error (inertia errors don't flow through flash).
          setChecked(audience === "all_crew")
          toast.error(t("missions.audience.flash.error"))
        },
        onFinish: () => setSubmitting(false),
      },
    )
  }

  function onCheckedChange(next: boolean) {
    if (submitting) return
    if (next) {
      // specific → all_crew: additive, no confirm.
      patchAudience("all_crew")
    } else {
      // all_crew → specific: confirm first (UX Spec §4).
      setConfirmOpen(true)
    }
  }

  return (
    <div
      className="flex items-center gap-2"
      data-testid="mission-audience-toggle"
    >
      <Switch
        id="mission-audience-toggle-switch"
        checked={checked}
        disabled={submitting}
        onCheckedChange={onCheckedChange}
        aria-describedby={HELPER_ID}
        data-testid="mission-audience-toggle-switch"
      />
      <div className="flex flex-col">
        <Label htmlFor="mission-audience-toggle-switch">
          {t("missions.audience.toggle.label")}
        </Label>
        <span id={HELPER_ID} className="text-muted-foreground text-xs">
          {t("missions.audience.toggle.helper")}
        </span>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="mission-audience-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("missions.audience.confirm.title")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("missions.audience.confirm.body")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>
              {t("missions.audience.confirm.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="mission-audience-confirm-action"
              disabled={submitting}
              // eslint-disable-next-line no-restricted-syntax -- closes on click by design (OKS-1023)
              onClick={() => {
                setConfirmOpen(false)
                patchAudience("specific")
              }}
            >
              {t("missions.audience.confirm.confirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
